import { ApiRequestError, type PayoutBalance } from "@shyne/shared/api";
import { api } from "@/lib/api";
import { activeOrganizationId } from "@/lib/active-organization";

export type StripeConnectStatusState =
  | { status: "no_account" }
  | {
      status: "pending";
      stripeAccountId: string;
      requirementsDue: string[];
      detailsSubmitted: boolean;
      checkedWithStripe: boolean;
    }
  | {
      status: "active";
      stripeAccountId: string;
      requirementsDue: string[];
      detailsSubmitted: boolean;
      checkedWithStripe: boolean;
      balance: PayoutBalance | null;
    };

const acting = () => ({ actingOrganization: activeOrganizationId() });

const paymentsUnavailable = (error: unknown): boolean => error instanceof ApiRequestError && error.code === "payments_unavailable";

async function balanceIfAvailable(): Promise<PayoutBalance | null> {
  try {
    return await api.payoutAccount.balance(acting());
  } catch (error) {
    if (paymentsUnavailable(error)) return null;
    throw error;
  }
}

export async function getOrganizationStripeStatus(): Promise<StripeConnectStatusState> {
  const { account } = await api.payoutAccount.get(acting());
  if (account === null) return { status: "no_account" };

  const known = {
    stripeAccountId: account.stripeAccountId,
    requirementsDue: account.requirementsDue,
    detailsSubmitted: account.detailsSubmitted === true,
    checkedWithStripe: account.checkedWithStripe,
  };
  if (account.payoutsEnabled !== true) return { status: "pending", ...known };
  return { status: "active", ...known, balance: await balanceIfAvailable() };
}

export async function createStripeAccountForOrganization(): Promise<string> {
  return (await api.payoutAccount.create(acting())).stripeAccountId;
}

export async function getStripeAccountLink(): Promise<string> {
  return (await api.payoutAccount.onboardingLink(acting())).url;
}

export async function getStripeDashboardLoginLink(): Promise<string> {
  return (await api.payoutAccount.dashboardLink(acting())).url;
}
